import type { Row } from '~/composables/useDb'

/** `inscriptionLabel` de la maquette. */
export function inscriptionLabel(i?: Row): string {
  if (!i) return '—'
  const etu = findById('personnes', i.etudiantId)
  const classe = i.classeId ? findById('classes', i.classeId) : null
  const annee = findById('annees', i.anneeId)
  const who = etu ? `${etu.prenoms} ${etu.nom}` : 'Étudiant inconnu'
  const cls = classe ? classe.libelle : 'sans classe'
  const an = annee ? annee.libelle : ''
  return `${who} (${cls}${an ? `, ${an}` : ''})`
}

const personLabel = (p: Row): string => `${p.prenoms} ${p.nom}`
const periodeLabel = (p: Row): string => {
  const a = findById('annees', p.anneeId)
  return `${p.libelle}${a ? `, ${a.libelle}` : ''}`
}

/** Les `display` qui sont des fonctions dans la maquette, par « entité.champ ». */
export const DISPLAY_FNS: Record<string, (r: Row) => string> = {
  'offres.niveauIds': (n) => {
    const c = findById('cycles', n.cycleId)
    return `${n.libelle}${c ? `, ${c.libelle}` : ''}`
  },
  'classes.professeurPrincipalId': personLabel,
  'liensParentaux.etudiantId': personLabel,
  'liensParentaux.parentId': personLabel,
  'inscriptions.etudiantId': personLabel,
  'historiqueStatutInscription.inscriptionId': inscriptionLabel,
  'reductionsAppliquees.inscriptionId': inscriptionLabel,
  'echeanciers.inscriptionId': inscriptionLabel,
  'lignesEcheance.echeancierId': (e) => {
    const insc = findById('inscriptions', e.inscriptionId)
    return insc ? inscriptionLabel(insc) : `Échéancier #${e.id}`
  },
  'versements.inscriptionId': inscriptionLabel,
  'utilisateurs.personneId': personLabel,
  'utilisateurRoles.utilisateurId': (u) => {
    const p = findById('personnes', u.personneId)
    return p ? `${p.prenoms} ${p.nom} (${u.login})` : String(u.login)
  },
  'utilisateurRoles.ecoleId': e => ecoleLabelText(e),
  'unitesEnseignement.periodeIds': periodeLabel,
  'matieres.ueId': u => `${u.code}, ${u.libelle}`,
  'matieres.periodeIds': periodeLabel,
  'notes.inscriptionId': inscriptionLabel,
  'notes.matiereId': m => `${m.libelle} (coef. ${m.coefficient})`,
  'bulletins.inscriptionId': inscriptionLabel,
  'documentsDossier.inscriptionId': inscriptionLabel,
  'cartesEtudiantes.inscriptionId': inscriptionLabel,
  'imputations.ligneEcheanceId': (l) => {
    const e = findById('echeanciers', l.echeancierId)
    const i = e ? findById('inscriptions', e.inscriptionId) : null
    return `${i ? i.numero : '?'}, ${l.libelle}`
  },
}

const anneeCourante = (p: Row): boolean => {
  const ca = currentAnnee()
  return !!ca && p.anneeId === ca.id
}

/** Les `filter` (fonctions) des champs de la maquette, par « entité.champ ». */
export const FILTER_FNS: Record<string, (r: Row) => boolean> = {
  'classes.professeurPrincipalId': p => p.role === 'ENSEIGNANT',
  'liensParentaux.etudiantId': p => p.role === 'ETUDIANT',
  'liensParentaux.parentId': p => p.role === 'PARENT',
  'inscriptions.etudiantId': p => p.role === 'ETUDIANT',
  'unitesEnseignement.periodeIds': anneeCourante,
  'matieres.periodeIds': anneeCourante,
}

/**
 * Les `computeValue` (fonctions) des champs de la maquette, par « entité.champ » : la valeur affichée dans le formulaire
 * n'est pas directement une propriété de la ligne, mais recalculée (ici, les niveaux d'une offre, portés par `niveauOffre`).
 */
export const COMPUTE_FNS: Record<string, (r: Row) => unknown> = {
  'offres.niveauIds': row => (useDb().value.niveauOffre ?? []).filter(no => no.offreId === row.id).map(no => no.niveauId),
}

/** Les `default` (fonctions) des champs de la maquette, par « entité.champ » : pré-remplis à l'ouverture du formulaire de création. */
export const DEFAULT_FNS: Record<string, () => unknown> = {
  'classes.anneeId': () => currentAnnee()?.id ?? null,
  'inscriptions.anneeId': () => currentAnnee()?.id ?? null,
  'tarifications.anneeId': () => currentAnnee()?.id ?? null,
  'periodes.anneeId': () => currentAnnee()?.id ?? null,
}
